import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Store } from '@ngxs/store';
import { SettingsState, ToggleTheme } from '@core/states/settings.state';

/**
 * @title Theme Service
 * @since 1.0.0
 *
 * @description This service manages the theme of the application, allowing users to toggle between light and dark modes.
 * It listens for system theme changes and updates the application's theme accordingly.
 */
@Injectable({
	providedIn: 'root',
})
export class ThemeService {

	/**
	 * A BehaviorSubject that holds the current dark mode state.
	 * It emits true if dark mode is enabled, false otherwise.
	 * @type {BehaviorSubject<boolean>}
	 * @since 1.0.0
	 */
	private darkModeSubject = new BehaviorSubject<boolean>(false);

	/**
	 * An observable that emits the current dark mode state.
	 * Components can subscribe to this observable to get updates on the dark mode state.
	 * @type {Observable<boolean>}
	 * @since 1.0.0
	 */
	public darkMode$ = this.darkModeSubject.asObservable();

	/**
	 * A MediaQueryList object that listens for changes in the user's system theme preference.
	 * It is used to detect when the user changes their system theme (dark/light mode).
	 * @type {MediaQueryList}
	 * @since 1.0.0
	 */
	private mediaQueryList: MediaQueryList;

	/**
	 * @param {Store} store - The NGXS store for managing application state.
	 * @since 1.0.0
	 */
	constructor(
		private store: Store
	) {
		// Initialize the dark mode subject with the current system theme preference
		this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
		// Listen for changes in the system theme preference
		this.mediaQueryList.addEventListener('change', this.handleSystemThemeChange.bind(this));
		// Initialize the dark mode subject based on the current system theme preference
		this.store.select(SettingsState.isDarkMode).subscribe(isDarkMode => {
			if (isDarkMode === undefined) {
				this.darkModeSubject.next(this.mediaQueryList.matches);
			} else {
				this.darkModeSubject.next(isDarkMode);
			}
			this.updateTheme();
		});
	}

	/**
	 * Handles changes in the user's system theme preference.
	 * It updates the dark mode state and applies the theme accordingly.
	 * @param {MediaQueryListEvent} event - The event object containing information about the media query change.
	 * @since 1.0.0
	 */
	private handleSystemThemeChange(event: MediaQueryListEvent) {
		this.darkModeSubject.next(event.matches);
		this.updateTheme();
	}

	/**
	 * Toggles the dark mode state.
	 * It dispatches an action to update the theme in the store and updates the dark mode subject.
	 * @since 1.0.0
	 */
	public toggleTheme() {
        const currentMode = this.darkModeSubject.value;
        this.store.dispatch(new ToggleTheme());
        this.darkModeSubject.next(!currentMode);
        this.updateTheme();
    }

	/**
	 * Updates the theme of the application based on the current dark mode state.
	 * It adds or removes the 'theme-dark' class from the document's root element.
	 * This class is used to apply dark mode styles to the application.
	 * @since 1.0.0
	 */
	private updateTheme() {
		const element = document.documentElement;
		if (this.darkModeSubject.value) {
			element.classList.add('theme-dark');
		} else {
			element.classList.remove('theme-dark');
		}
	}
}